export type GeocodeAddress = {
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country: string;
};

export type GeocodeResult = {
  latitude: number;
  longitude: number;
  label: string;
  address: GeocodeAddress;
};

type NominatimAddress = {
  road?: string;
  house_number?: string;
  postcode?: string;
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  hamlet?: string;
  country?: string;
};

function mapAddress(address: NominatimAddress | undefined): GeocodeAddress {
  return {
    street: address?.road ?? '',
    houseNumber: address?.house_number ?? '',
    postalCode: address?.postcode ?? '',
    city: address?.city ?? address?.town ?? address?.village ?? address?.municipality ?? address?.hamlet ?? '',
    country: address?.country ?? '',
  };
}

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

function toQuery(query: Record<string, string>) {
  return Object.entries(query)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
}

export async function geocodeAddress(query: string, signal?: AbortSignal): Promise<GeocodeResult[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const url = `${NOMINATIM_URL}?${toQuery({
    format: 'json',
    q: trimmed,
    addressdetails: '1',
    'accept-language': 'de',
    limit: '5',
  })}`;

  const res = await fetch(url, {
    method: 'GET',
    signal,
    headers: {
      // Nominatim requires a User-Agent in production. Browser sets one automatically.
      'Accept': 'application/json',
    },
  });

  if (!res.ok) {
    throw new Error('Fehler bei der Adresssuche');
  }

  const data = (await res.json()) as Array<{
    lat: string;
    lon: string;
    display_name: string;
    address?: NominatimAddress;
  }>;

  return data
    .map((item) => ({
      latitude: Number(item.lat),
      longitude: Number(item.lon),
      label: item.display_name,
      address: mapAddress(item.address),
    }))
    .filter((x) => Number.isFinite(x.latitude) && Number.isFinite(x.longitude));
}
